import type { Dispatch, ElementType, SetStateAction } from "react";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { pluralise } from "simplydo/core/helpers";
import { styled } from "simplydo/react/styled";
import { Icon, Progress, type SemanticICONS } from "simplydo/react/ui";

import { type DragFile, type UploadState, verifyType } from "./uploadUtils";

const StyledDropZone = styled.div`
  border: 2px dashed #ccc;
  border-radius: 10px;
  padding: 20px 40px;
`;

const DropZoneStyling = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 20px;

  > div {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }

  > h5,
  > div > h5,
  > div > h3 {
    margin: 0 0 3px 0;
    display: block;
    color: #808080;
    > u {
      color: ${(props) =>
        props.theme ?
          props.theme.primaryColour
        : "var(--colorBrandForegroundLink, #0078d4)"};
      cursor: pointer;
    }
  }
  > i.icon {
    color: #808080;
    height: 1em !important;
  }
`;

export type DropZoneConfigProps = {
  DropZoneComponent?: ElementType;
  title?: string;
  subtitle?: string;
  defaultIcon?: SemanticICONS;
  onClick?: () => void;
};

export type DropZoneProps = {
  uploadState: UploadState;
  setUploadState: Dispatch<SetStateAction<UploadState>>;
  accept?: string;
  disabled?: boolean;
  draggedOverFiles?: Array<DragFile>;
  allowMultiple?: boolean;
  style?: React.CSSProperties;
} & DropZoneConfigProps;

export const DropZone = ({
  disabled,
  accept,
  uploadState,
  draggedOverFiles,
  allowMultiple,
  style,
  onClick,
  title,
  subtitle,
  defaultIcon = "upload",
  DropZoneComponent = StyledDropZone,
}: DropZoneProps) => {
  if (uploadState.isUploading) {
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="huge" name="spinner" />
          <Progress
            percent={uploadState.uploadProgress}
            indicating
            progress
            style={{ width: "100%" }}
          >
            Uploading...
          </Progress>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  if (disabled) {
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="huge" name="ban" />
          <h5>Cannot upload more files</h5>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  if ((draggedOverFiles?.length ?? 0) > 0) {
    if (!allowMultiple && (draggedOverFiles?.length ?? 0) > 1) {
      return (
        <DropZoneComponent style={style}>
          <DropZoneStyling>
            <Icon size="huge" name="ban" />
            <h5>Cannot upload more than one file</h5>
          </DropZoneStyling>
        </DropZoneComponent>
      );
    }
    if (accept && draggedOverFiles) {
      for (let i = 0; i < draggedOverFiles.length; i++) {
        const draggedFile = draggedOverFiles[i];
        if (!draggedFile) continue;
        const typeCheck = verifyType(accept, draggedFile.type);
        if (!typeCheck.allowed) {
          return (
            <DropZoneComponent style={style}>
              <DropZoneStyling>
                <Icon size="huge" name="ban" />
                <h5>{typeCheck.error}</h5>
              </DropZoneStyling>
            </DropZoneComponent>
          );
        }
      }
    }
    return (
      <DropZoneComponent style={style}>
        <DropZoneStyling>
          <Icon size="huge" name="check circle" />
          <h5>
            Drop the {pluralise(draggedOverFiles?.length ?? 0, "file", "files")}{" "}
            to start the upload
          </h5>
        </DropZoneStyling>
      </DropZoneComponent>
    );
  }

  return (
    <DropZoneComponent style={style} onClick={onClick}>
      <DropZoneStyling>
        <Icon size="huge" name={defaultIcon} />
        <div>
          {title ?
            <h3>{title}</h3>
          : null}
          {subtitle ?
            <h5>{subtitle}</h5>
          : null}
          <h5 style={{ marginTop: 6 }}>
            Drop a file here to upload, or{" "}
            <u
              role="button"
              tabIndex={0}
              onKeyDown={handleClickForKeyboard(onClick)}
            >
              click to browse
            </u>
            .{allowMultiple ? " You can upload multiple files at once." : ""}
          </h5>
        </div>
      </DropZoneStyling>
    </DropZoneComponent>
  );
};
